/** Small formatters shared by the dashboard cards. */
import { parseDateOnly } from "@/lib/dateOnly";

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Whole calendar days from `now` to `iso` (negative when past). */
export const calendarDays = (
  iso: string | null | undefined,
  now: Date = new Date()
): number | null => {
  if (!iso) return null;
  const d = parseDateOnly(iso);
  if (!d) return null;
  return Math.round((startOfDay(d) - startOfDay(now)) / DAY_MS);
};

export const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  // A calendar date (midnight UTC) is that date, not the evening before.
  const d = parseDateOnly(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const shortTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * When something is due, relative to today: "Overdue 3d", "Today", "Today
 * 2:30 PM", "Tomorrow", "In 5d", or the short date further out.
 */
export const dueLabel = (
  iso: string,
  opts: { allDay?: boolean; now?: Date } = {}
): string => {
  const now = opts.now ?? new Date();
  const days = calendarDays(iso, now);
  if (days === null) return "";
  if (days < 0) return `Overdue ${-days}d`;
  if (days === 0) {
    const t = opts.allDay ? "" : shortTime(iso);
    return t ? `Today ${t}` : "Today";
  }
  if (days === 1) return "Tomorrow";
  if (days <= 14) return `In ${days}d`;
  return shortDate(iso);
};

/** "Expired 3d ago", "Expires today", "Expires in 41d", or the date. */
export const expiryLabel = (iso: string, now: Date = new Date()): string => {
  const days = calendarDays(iso, now);
  if (days === null) return "";
  if (days < 0) return `Expired ${-days}d ago`;
  if (days === 0) return "Expires today";
  if (days <= 90) return `Expires in ${days}d`;
  return `Expires ${shortDate(iso)}`;
};

/** Colour class for a days-left figure. */
export const urgencyClass = (
  daysLeft: number | null | undefined,
  overdue = false
): string => {
  if (overdue || (typeof daysLeft === "number" && daysLeft < 0))
    return "text-danger";
  if (typeof daysLeft === "number" && daysLeft <= 30) return "text-warn";
  return "text-fg-3";
};

/** "awaiting_review" -> "Awaiting review". */
export const humanizeStatus = (status: string | null | undefined): string => {
  const s = (status ?? "").trim().replace(/[_-]+/g, " ");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

/**
 * Pull the label, href and title out of a markdown ref
 * (`[label](path "Kind · detail")`). Null when not a ref.
 */
export const parseRefMarkdown = (
  ref: string | null | undefined
): { label: string; href: string; title: string | null } | null => {
  const m = /^\[((?:[^[\]\\]|\\.)*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)$/.exec(
    (ref ?? "").trim()
  );
  if (!m) return null;
  return {
    label: m[1].replace(/\\([[\]])/g, "$1"),
    href: m[2],
    title: m[3] ?? null
  };
};
